import React, { useState, useEffect, useCallback, useRef } from 'react';
import { IoChatbubbleEllipses, IoClose, IoSend } from 'react-icons/io5';
import { useAuth } from './AuthContext';
import { useMessages, useConversations } from '../hooks/useMessages';

const ChatButton = ({ productId, productName, productImage }: { productId?: string; productName?: string; productImage?: string }) => {
  const { currentUser, isLoggedIn, loading: authLoading } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const { messages, sendMessage, error: messageError } = useMessages(conversationId || undefined);
  const { conversations, createConversation } = useConversations(currentUser?.uid, false);
  
  // Get unread count for current conversation
  const currentConversation = conversations.find(c => c.id === conversationId);
  const unreadCount = currentConversation?.userUnreadCount || 0;

  // console.log('ChatButton render - conversationId:', conversationId, 'messages count:', messages.length);

  const initializeConversation = useCallback(async () => {
    if (!currentUser) {
      // console.log('Cannot initialize conversation: no current user');
      return;
    }

    try {
      // console.log('Creating conversation for user:', currentUser.uid);
      // Try to find existing conversation or create new one
      const newConversationId = await createConversation(
        currentUser.uid,
        currentUser.email || 'Unknown',
        currentUser.displayName || 'Anonymous User'
      );
      // console.log('Conversation created with ID:', newConversationId);
      setConversationId(newConversationId);
    } catch (err: any) {
      console.error('Error creating conversation:', err);
      alert(`Failed to initialize chat: ${err.message}`);
    }
  }, [currentUser, createConversation]);

  // Initialize conversation only when chat is opened (not on page load)
  useEffect(() => {
    if (isOpen && isLoggedIn && currentUser && !conversationId) {
      // console.log('ChatButton: Starting conversation initialization');
      initializeConversation();
    }
  }, [isOpen, isLoggedIn, currentUser, conversationId, initializeConversation]);

  // Auto-scroll messages container to bottom
  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!messageText.trim()) {
      // console.log('Message text is empty');
      return;
    }

    if (!conversationId) {
      console.error('No conversation ID set');
      alert('Chat not initialized. Please refresh the page.');
      return;
    }

    if (!currentUser) {
      console.error('No current user');
      alert('You are not logged in.');
      return;
    }

    setSending(true);
    try {
      // console.log('Sending message to conversation:', conversationId);
      await sendMessage(
        messageText,
        conversationId,
        currentUser.uid,
        currentUser.displayName || 'User',
        currentUser.email || 'unknown@email.com',
        false,
        productId,
        productName,
        productImage
      );
      // console.log('Message sent successfully');
      setMessageText('');
    } catch (err: any) {
      console.error('Error sending message:', err);
      alert(`Failed to send message: ${err.message || 'Unknown error'}`);
    } finally {
      setSending(false);
    }
  };

  // Don't show button while auth is loading
  if (authLoading) {
    return null;
  }

  // Show login button if not logged in
  if (!isLoggedIn) {
    return (
      <div className="fixed bottom-6 right-6 z-40">
        <button
          className="flex items-center gap-2 bg-zinc-800 text-white px-4 py-3 rounded-full shadow-lg hover:bg-zinc-700 transition-all hover:shadow-xl"
          onClick={() => (window.location.href = '/login')}
        >
          <IoChatbubbleEllipses size={24} />
          <span className="text-sm font-medium">Login to Chat</span>
        </button>
      </div>
    );
  }

  return (
    <>
      {/* Floating Chat Button */}
      {!isOpen && (
        <div className="fixed bottom-6 right-6 z-40">
          <button
            onClick={() => setIsOpen(true)}
            className="flex items-center gap-2 bg-zinc-800 text-white px-4 py-3 rounded-full shadow-lg hover:bg-zinc-700 transition-all hover:shadow-xl group"
          >
            <IoChatbubbleEllipses size={24} className="group-hover:scale-110 transition-transform" />
            <span className="text-sm font-medium hidden sm:inline">Chat with Admin</span>
          </button>
          
          {/* Unread Badge */}
          {unreadCount > 0 && (
            <div className="absolute top-0 right-0 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center shadow-lg">
              {unreadCount > 9 ? '9+' : unreadCount}
            </div>
          )}
        </div>
      )}

      {/* Chat Modal */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 z-50 w-96 max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col max-h-[600px]">
          {/* Header */}
          <div className="bg-gradient-to-r from-zinc-800 to-zinc-700 text-white p-4 flex items-center justify-between">
            <div>
              <h3 className="font-bold text-lg">Chat with Admin</h3>
              {productName && (
                <p className="text-xs text-yellow-200 mt-1">About: {productName}</p>
              )}
              {!productName && (
                <p className="text-xs text-gray-300">We'll respond as soon as possible</p>
              )}
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1 hover:bg-white hover:bg-opacity-20 rounded-lg transition-all"
            >
              <IoClose size={24} />
            </button>
          </div>

          {/* Messages Container */}
          <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messageError ? (
              <div className="text-center text-red-500 py-8">
                <p className="text-sm font-semibold">Error loading messages</p>
                <p className="text-xs mt-2">{messageError}</p>
                {messageError.includes('index') && (
                  <p className="text-xs text-yellow-600 mt-3 font-semibold">
                    Check your browser console for a link to create the required Firestore index
                  </p>
                )}
              </div>
            ) : messages.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                <p className="text-sm">No messages yet. Start the conversation!</p>
              </div>
            ) : (
              <>
                {messages.map((msg) => (
                  <div key={msg.id}>
                    {/* Product Info Card */}
                    {msg.productId && (
                      <div className="mb-3 flex justify-end">
                        <div className="max-w-xs bg-yellow-50 border border-yellow-200 rounded-lg overflow-hidden">
                          {msg.productImage && (
                            <img 
                              src={msg.productImage} 
                              alt={msg.productName}
                              className="w-full h-32 object-cover"
                            />
                          )}
                          <div className="p-3">
                            <p className="text-xs font-semibold text-gray-600">Asking about:</p>
                            <p className="font-bold text-zinc-800 text-sm">{msg.productName}</p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Message Bubble */}
                    <div
                      className={`flex ${msg.isAdmin ? 'justify-start' : 'justify-end'}`}
                    >
                      <div
                        className={`max-w-xs px-4 py-2 rounded-lg text-sm ${
                          msg.isAdmin
                            ? 'bg-white border border-gray-200 text-gray-800'
                            : 'bg-zinc-800 text-white'
                        }`}
                      >
                        <p className="font-semibold text-xs mb-1 opacity-75">
                          {msg.senderName}
                        </p>
                        <p className="break-words">{msg.message}</p>
                        <p className="text-xs opacity-60 mt-1">
                          {msg.createdAt?.toDate?.().toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* Message Input */}
          <form onSubmit={handleSendMessage} className="border-t border-gray-200 p-4 bg-white">
            <div className="flex gap-2">
              <input
                type="text"
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                placeholder="Type your message..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-zinc-800 focus:border-transparent outline-none text-sm"
                disabled={sending}
              />
              <button
                type="submit"
                disabled={sending || !messageText.trim()}
                className="bg-zinc-800 text-white px-4 py-2 rounded-lg hover:bg-zinc-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <IoSend size={16} />
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default ChatButton;
